import type { ReactNode } from 'react';
import type { StatusValue } from '../../api/types';
import { useResolveText } from '../../composition/RunnerTokenContext';
import { Icon, type IconName } from './Icon';
import s from './ui.module.css';

export const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

export const ui = s;

interface BlockProps {
  icon?: IconName;
  title: string;
  lead?: string;
  presenting?: boolean;
  headerRight?: ReactNode;
  children: ReactNode;
}

export function Block({ icon, title, lead, presenting, headerRight, children }: BlockProps) {
  // Authored section heading/lead may carry `{{group.key}}` tokens; resolve them per-delivery.
  const resolve = useResolveText();
  return (
    <section className={cx(s.block, presenting && s.blockLg)}>
      <div className={cx(s.blockHead, presenting && s.blockHeadLg)}>
        {icon && (
          <div className={cx(s.iconChip, presenting && s.iconChipLg)}>
            <Icon name={icon} size={presenting ? 18 : 14} />
          </div>
        )}
        <h4>{resolve(title)}</h4>
        {headerRight}
      </div>
      {lead && <p className={s.blockLead}>{resolve(lead)}</p>}
      {children}
    </section>
  );
}

const STATUS_CLASS: Record<StatusValue, string> = {
  done: s.spDone,
  progress: s.spProgress,
  notstarted: s.spNotstarted,
};

export const STATUS_TEXT: Record<StatusValue, string> = {
  done: 'Done',
  progress: 'In progress',
  notstarted: 'Not started',
};

export function StatusPill({ status, className }: { status: StatusValue; className?: string }) {
  return (
    <span className={cx(s.statusPill, STATUS_CLASS[status], className)}>{STATUS_TEXT[status]}</span>
  );
}

const SEG_SELECTED: Record<StatusValue, string> = {
  done: s.selDone,
  progress: s.selProgress,
  notstarted: s.selNotstarted,
};

const SEG_ORDER: StatusValue[] = ['done', 'progress', 'notstarted'];

interface SegmentedProps {
  value: StatusValue;
  onChange: (value: StatusValue) => void;
  /** Announced to screen readers; the colour alone does not carry meaning. */
  label: string;
}

export function SegmentedStatus({ value, onChange, label }: SegmentedProps) {
  return (
    <div className={s.seg} role="group" aria-label={`Status for ${label}`}>
      {SEG_ORDER.map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={v === value}
          className={cx(v === value && SEG_SELECTED[v])}
          onClick={() => onChange(v)}
        >
          {STATUS_TEXT[v]}
        </button>
      ))}
    </div>
  );
}

export function ProgressBar({ done, total, unit }: { done: number; total: number; unit: string }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className={s.readinessBar}>
      <div
        className={s.readinessTrack}
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${done} of ${total} ${unit}`}
      >
        <div className={s.readinessFill} style={{ width: `${pct}%` }} />
      </div>
      <div className={s.readinessLabel}>
        {done}/{total} {unit}
      </div>
    </div>
  );
}

export interface ChecklistEntry {
  key: string;
  label: string;
  role?: 'must' | 'should' | 'nice';
  /** A line under the label saying what the item actually covers. */
  hint?: string;
}

const ROLE_LABEL = { must: 'Must-have', should: 'Should-have', nice: 'Nice-to-have' } as const;
const ROLE_CLASS = { must: s.must, should: s.should, nice: s.nice } as const;

interface ChecklistProps {
  items: ChecklistEntry[];
  checked: (key: string) => boolean;
  onToggle: (key: string) => void;
  presenting?: boolean;
}

export function Checklist({ items, checked, onToggle, presenting }: ChecklistProps) {
  return (
    <ul className={cx(s.checklist, presenting && s.checklistLg)}>
      {items.map((item) => {
        const isChecked = checked(item.key);
        return (
          <li key={item.key} className={cx(isChecked && s.checked)}>
            <label className={s.checklistLabel}>
              <input type="checkbox" checked={isChecked} onChange={() => onToggle(item.key)} />
              <span>
                {item.label}
                {item.hint && <em className={s.checklistHint}>{item.hint}</em>}
              </span>
            </label>
            {item.role && (
              <span className={cx(s.roleTag, ROLE_CLASS[item.role])}>{ROLE_LABEL[item.role]}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
